/**
 * Utility functions for React applications, including JSON conversion for shopping bag payload
 */

/**
 * Converts input JSON data to required shopping bag payload format
 * @param {Object} inputData - Input JSON data with items array
 * @param {string} [accountID=''] - Optional BOthebotAccountID
 * @returns {Object} Formatted payload or empty object on error
 */
export const AddToCartDataFormatter = (inputData, accountID = "") => {
    try {
        // Input validation
        if (!inputData || typeof inputData !== "object" || !Array.isArray(inputData.items)) {
            console.error("Invalid input data: Input must be an object with an items array");
            return {};
        }

        const { items } = inputData;

        // If no items, return empty payload
        if (items.length === 0) {
            return {
                BOthebotAccountID: accountID,
                shopperBag: { items: [] }
            };
        }

        // Map items to required format
        const formattedItems = items
            .flatMap(item => {
                const { BOTagData, BOTagAlgorithmData, UserAddress, UserGPSCoordinates } = item;

                // Validate required sections for each item
                if (!BOTagData || !BOTagAlgorithmData || !UserAddress || !UserGPSCoordinates) {
                    console.warn(`Skipping item ${item.itemID}: Missing required data sections`);
                    return [];
                }

                // Create deliveryInstructions object
                const deliveryInstructions = {
                    latitude: UserGPSCoordinates.latitude || 0,
                    longitude: UserGPSCoordinates.longitude || 0,
                    countryName: UserAddress.country || "",
                    city: UserAddress.city || "",
                    address: UserAddress.street || "",
                    doorNumber: UserAddress.streetNumber || "",
                    floor: UserAddress.floor || "",
                    postcode: UserAddress.postCode || "",
                    recipientFirstName: UserAddress.recipientFirstName || "",
                    recipientLastName: UserAddress.recipientLastName || "",
                    recipientCompanyName: UserAddress.recipientCompanyName || "",
                    courierInstructions: UserAddress.courierInstructions || ""
                };

                // Create userLocation object
                const userLocation = {
                    latitude: UserGPSCoordinates.latitude || 0,
                    longitude: UserGPSCoordinates.longitude || 0,
                    countryName: UserAddress.country || "",
                    city: UserAddress.city || "",
                    address: UserAddress.street || "",
                    doorNumber: UserAddress.streetNumber || "",
                    floor: UserAddress.floor || "",
                    postcode: UserAddress.postCode || ""
                };

                // Create formatted item
                const formattedItem = {
                    GTINbarcode: BOTagData.GTINbarcode || "",
                    labelBrandID: BOTagData.labelBrandID || "",
                    storeID: BOTagData.storeDetails || "",
                    originalItemPrice: BOTagAlgorithmData.price || 0,
                    currentItemPrice: BOTagAlgorithmData.price || 0, // Assuming no discount unless specified
                    itemDiscountPercentage: BOTagAlgorithmData.discountPercentage || 0,
                    deliveryOption: BOTagAlgorithmData.type
                        ? BOTagAlgorithmData.type.charAt(0).toUpperCase() + BOTagAlgorithmData.type.slice(1)
                        : "",
                    deliveryCost: BOTagAlgorithmData.deliveryCharges || 0,
                    courierID: BOTagAlgorithmData.cf?._id || "",
                    shippingID: BOTagAlgorithmData._id || "",
                    deliveryInstructions,
                    userLocation,
                    deliveryPreferences: BOTagData.deliveryPreference
                        ? BOTagData.deliveryPreference.charAt(0).toUpperCase() + BOTagData.deliveryPreference.slice(1)
                        : "",
                    BOimageID: BOTagData.images?.[0]?._id || "",
                    brandDetails: {
                        logos: BOTagData?.logoURL,
                        name: BOTagData?.name
                    },
                    productDetails: {
                        color: BOTagData?.selectedColorName,
                        colorCode: BOTagData?.selectedColor,
                        images: BOTagData?.images,
                        name: BOTagData?.productName,
                        referenceCode: "",
                        size: BOTagData?.selectedSize,
                        type: BOTagData?.productType
                    }
                };

                // Duplicate item based on quantity
                return Array(BOTagData.selectedQuantity).fill(formattedItem);
            })
            .filter(item => item !== null); // Remove invalid items

        // Create final payload
        return {
            BOthebotAccountID: accountID,
            shopperBag: {
                name: "bag",
                items: formattedItems
            }
        };
    } catch (error) {
        console.error("Error converting to shopping bag format:", error);
        return {};
    }
};

export const transformData = inputDataArray => {
    return inputDataArray.map(inputData => ({
        GTINbarcode: inputData.GTINbarcode,
        labelBrandID: inputData.labelBrandID,
        originalItemPrice: inputData.originalItemPrice,
        currentItemPrice: inputData.currentItemPrice,
        itemDiscountPercentage: inputData.itemDiscountPercentage,
        deliveryOption: inputData.deliveryOption,
        deliveryCost: inputData.deliveryCost,
        courierID: inputData.courierDetails ? inputData.courierDetails.id || "" : "",
        shippingID: inputData.shippingDetails ? inputData.shippingDetails.id || "" : "",
        storeID: inputData?.storeID?._id ? inputData?.storeID?._id : inputData?.storeID,
        deliveryInstructions: {
            latitude: inputData.deliveryInstructions.latitude,
            longitude: inputData.deliveryInstructions.longitude,
            countryName: inputData.deliveryInstructions.countryName,
            city: inputData.deliveryInstructions.city,
            address: inputData.deliveryInstructions.address,
            doorNumber: inputData.deliveryInstructions.doorNumber,
            floor: inputData.deliveryInstructions.floor,
            postcode: inputData.deliveryInstructions.postcode,
            recipientFirstName: inputData.deliveryInstructions.recipientFirstName,
            recipientLastName: inputData.deliveryInstructions.recipientLastName,
            recipientCompanyName: inputData.deliveryInstructions.recipientCompanyName,
            courierInstructions: inputData.deliveryInstructions.courierInstructions
        },
        userLocation: {
            latitude: inputData.userLocation.latitude,
            longitude: inputData.userLocation.longitude,
            countryName: inputData.userLocation.countryName,
            city: inputData.userLocation.city,
            address: inputData.userLocation.address,
            doorNumber: inputData.userLocation.doorNumber,
            floor: inputData.userLocation.floor,
            postcode: inputData.userLocation.postcode
        },
        deliveryPreferences: inputData.deliveryPreferences,
        BOimageID: inputData.BOimageID
    }));
};
